import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import trackingService, {
  AvailableCarrier,
  TrackingLinkRow,
  TrackingLinkStatus,
} from '../../services/tracking.service';
import { TanTable } from '../../components/TanTable';

/**
 * "Tracking Links" admin page (Tracing → Tracking Links).
 *
 * Clears `order_tracking_link` rows left behind after a container is deleted
 * in gwiTrace — the second half of a true delete-and-resubmit.
 *
 * WHY BOTH HALVES ARE NEEDED. Deleting the gwiTrace shipment alone leaves the
 * link row pointing at a dead id. Deleting the link row alone leaves the
 * shipment alive, so the next import REJOINS it and carries the stale trace
 * forward — exactly what the reset was meant to clear.
 *
 * WHY THE ORDER IS gwiTrace FIRST. gwiTrace's delete endpoints require a
 * browser session in its admin AD group; gwi360's service credential cannot
 * call them. So the operator deletes there, and this page cleans up here.
 *
 * That constraint produced a safer design than a single button would have.
 * An orphan is VERIFIABLE: the backend asks gwiTrace whether each shipment
 * still exists and refuses to delete a link whose shipment is alive. Only
 * `orphaned` rows are selectable, and the backend re-checks at delete time,
 * so a shipment re-created between page load and click is still protected.
 *
 * `unchecked` appears when the existence probe fails — a gwiTrace outage must
 * not paint every row as a safe orphan, so those rows are never deletable.
 */

const STATUS_STYLES: Record<TrackingLinkStatus, { label: string; cls: string; hint: string }> = {
  orphaned: {
    label: 'Orphaned',
    cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    hint: 'Shipment is gone from gwiTrace — safe to delete this link.',
  },
  linked: {
    label: 'Still linked',
    cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    hint: 'Shipment is alive in gwiTrace. Delete it there first.',
  },
  unchecked: {
    label: 'Unchecked',
    cls: 'bg-gray-100 text-label dark:bg-gray-800',
    hint: 'Could not reach gwiTrace to verify — not deletable until it responds.',
  },
};

// Move type is joined from CMS, so it is the one column here that can be
// legitimately EMPTY: a PRO deleted or archived out of `orderscurrent` still
// leaves its link row behind — and that row is precisely what an operator
// comes here to clear. Rendered as an explicit dash rather than blank so the
// gap reads as "no CMS order" instead of a rendering fault.
//
// Only IMPORT and EXPORT are tinted. CMS also uses INBOUND / OUTBOUND / REPO
// / DOMESTIC, and those fall through to the neutral style rather than being
// hidden — an unexpected move type on a tracking link is worth noticing, not
// smoothing over.
const MOVETYPE_STYLES: Record<string, string> = {
  IMPORT: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  EXPORT: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
};
const MOVETYPE_NEUTRAL = 'bg-gray-100 text-label dark:bg-gray-800';

const PAGE_SIZE = 100;

// Server caps: the list endpoint allows 100 per page (one gwiTrace batch-get
// per page, and batch-get itself caps at 100), and a bulk delete allows 500.
// Both stay as they are; the CLIENT pages and chunks instead.
const DELETE_CHUNK = 500;
// A stop so a mis-set filter cannot enumerate the whole table by accident.
const SWEEP_CAP = 30000;

const formatSynced = (iso: string | null): string => {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60_000);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 48) return `${diffHour}h ago`;
  return `${Math.floor(diffHour / 24)}d ago`;
};

const TrackingLinks: React.FC = () => {
  const [carrier, setCarrier] = useState<string>('');
  const [containers, setContainers] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'orphaned' | 'linked'>('all');
  const [offset, setOffset] = useState(0);

  const [rows, setRows] = useState<TrackingLinkRow[]>([]);
  const [total, setTotal] = useState(0);
  const [probeFailed, setProbeFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState<TrackingLinkRow[]>([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkNote, setBulkNote] = useState('');
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const [carriers, setCarriers] = useState<AvailableCarrier[]>([]);

  useEffect(() => {
    trackingService
      .listAvailableCarriers()
      .then(setCarriers)
      .catch(() => setCarriers([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await trackingService.listTrackingLinks({
        carrier: carrier || undefined,
        containers: containers.trim() || undefined,
        status: statusFilter,
        limit: PAGE_SIZE,
        offset,
      });
      setRows(data.links);
      setTotal(data.total);
      setProbeFailed(data.probe_failed);
      setSelected([]);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load tracking links');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [carrier, containers, statusFilter, offset]);

  useEffect(() => { load(); }, [load]);

  // Only orphans can be deleted; the backend enforces this too.
  const deletable = useMemo(
    () => selected.filter((r) => r.status === 'orphaned'),
    [selected],
  );
  const blockedCount = selected.length - deletable.length;

  const onDelete = useCallback(async () => {
    if (!deletable.length) return;
    const preview = deletable.slice(0, 8).map((r) => r.submitted_container || r.pronumber).join(', ');
    const more = deletable.length > 8 ? ` and ${deletable.length - 8} more` : '';
    if (!window.confirm(
      `Delete ${deletable.length} orphaned tracking link${deletable.length === 1 ? '' : 's'}?\n\n`
      + `${preview}${more}\n\n`
      + 'These links point at shipments that no longer exist in gwiTrace. '
      + 'Deleting them lets the order be re-submitted as a fresh trace. '
      + 'The CMS orders themselves are NOT touched.',
    )) return;

    setDeleting(true);
    try {
      const res = await trackingService.deleteTrackingLinks(deletable.map((r) => r.pronumber));
      const parts = [`Deleted ${res.deleted.length} link${res.deleted.length === 1 ? '' : 's'}`];
      if (res.still_linked.length) {
        parts.push(`${res.still_linked.length} skipped — shipment still in gwiTrace`);
      }
      if (res.not_found.length) parts.push(`${res.not_found.length} already gone`);
      setToast({ kind: 'success', msg: parts.join(' · ') });
      await load();
    } catch (e: any) {
      setToast({
        kind: 'error',
        msg: e?.response?.data?.detail || e?.message || 'Delete failed',
      });
    } finally {
      setDeleting(false);
    }
  }, [deletable, load]);

  // Delete EVERY orphaned link the current filters match, not just the page.
  //
  // The selection bar only ever reaches the rendered 100 rows, and `load()`
  // clears the selection on every page change, so clearing a terminal's fleet
  // meant 100 at a time. This pages the list itself, collects the rows the
  // server has already marked `orphaned`, and deletes them in chunks.
  //
  // It relies on the SAME orphan verdict as the manual path — the server
  // probes gwiTrace per page and refuses at delete time anyway — so nothing
  // here can remove a link whose shipment is alive.
  const onDeleteAllMatching = useCallback(async () => {
    setBulkBusy(true);
    setBulkNote('Finding orphaned links…');
    try {
      const pros: number[] = [];
      let at = 0;
      let seen = 0;
      let reportedTotal = 0;

      for (;;) {
        const page = await trackingService.listTrackingLinks({
          carrier: carrier || undefined,
          containers: containers.trim() || undefined,
          // 'all', NOT 'orphaned'. The server applies the status filter AFTER
          // paging and probing, so asking for orphans returns a SHORT page
          // whenever some rows were linked — and "short page means the end"
          // would then stop early and silently under-delete. Page the
          // unfiltered set, filter here.
          status: 'all',
          limit: PAGE_SIZE,
          offset: at,
        });
        if (page.probe_failed) {
          throw new Error(
            'gwiTrace could not be reached, so orphan status is unverified — '
            + 'nothing was deleted.',
          );
        }
        // `total` is the count matching the FILTERS, before status filtering —
        // an upper bound on what we will scan, which is what the cap wants.
        reportedTotal = page.total || 0;
        if (reportedTotal > SWEEP_CAP) {
          throw new Error(
            `${reportedTotal.toLocaleString()} links match. Narrow the filter first.`,
          );
        }
        const links = page.links || [];
        if (links.length === 0) break;
        seen += links.length;
        for (const l of links) if (l.status === 'orphaned') pros.push(l.pronumber);
        setBulkNote(`Found ${pros.length} orphaned (scanned ${seen})…`);
        // Valid only because we asked for 'all': a short page really is the
        // last one. It would NOT be safe with a status filter applied.
        if (links.length < PAGE_SIZE) break;
        at += PAGE_SIZE;
      }

      if (pros.length === 0) {
        setToast({ kind: 'success', msg: 'No orphaned links matched.' });
        return;
      }

      if (!window.confirm(
        `Delete ${pros.length} orphaned tracking link${pros.length === 1 ? '' : 's'}?\n\n`
        + 'Every one points at a shipment that no longer exists in gwiTrace. '
        + 'Deleting them lets those orders be re-submitted as fresh traces. '
        + 'The CMS orders themselves are NOT touched.',
      )) return;

      let deleted = 0;
      let stillLinked = 0;
      let notFound = 0;
      for (let i = 0; i < pros.length; i += DELETE_CHUNK) {
        const chunk = pros.slice(i, i + DELETE_CHUNK);
        setBulkNote(`Deleting ${Math.min(i + chunk.length, pros.length)}/${pros.length}…`);
        // Not caught per-chunk on purpose: a failure partway through has
        // already deleted the earlier chunks, and the count below says so.
        const res = await trackingService.deleteTrackingLinks(chunk);
        deleted += res.deleted.length;
        stillLinked += res.still_linked.length;
        notFound += res.not_found.length;
      }

      const parts = [`Deleted ${deleted} link${deleted === 1 ? '' : 's'}`];
      if (stillLinked) parts.push(`${stillLinked} skipped — shipment still in gwiTrace`);
      if (notFound) parts.push(`${notFound} already gone`);
      setToast({ kind: 'success', msg: parts.join(' · ') });
      setOffset(0);
      await load();
    } catch (e: any) {
      setToast({
        kind: 'error',
        msg: e?.response?.data?.detail || e?.message || 'Bulk delete failed',
      });
    } finally {
      setBulkBusy(false);
      setBulkNote('');
    }
  }, [carrier, containers, load]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 6000);
    return () => clearTimeout(t);
  }, [toast]);

  const columns = useMemo<ColumnDef<TrackingLinkRow>[]>(() => [
    { accessorKey: 'pronumber', header: 'Pro #', size: 100 },
    { accessorKey: 'submitted_container', header: 'Container', size: 140 },
    // ONE column for both, because a row never has both.
    //
    // On an IMPORT link the B/L is the half of gwiTrace's dedupe key
    // (container + B/L) that actually moves — a CMS-side correction repoints
    // the link to a NEW shipment, so the same container against two different
    // B/Ls is what that looks like here. On an EXPORT link the equivalent
    // identifier is the BOOKING, and the B/L is null.
    //
    // They are mutually exclusive by construction: the import path writes a
    // null booking, the export path writes a null B/L. Verified across 20,537
    // rows on test and prod — zero carry both — so coalescing loses nothing
    // and spares the table a column that is blank for every row of one kind.
    //
    // accessorFn (not accessorKey) so the column's own filter box and sort
    // see the same merged value the cell renders.
    {
      id: 'submitted_bill_of_lading',
      accessorFn: (r) => r.submitted_bill_of_lading || r.submitted_booking_number || '',
      header: 'BOL / BKG',
      size: 170,
    },
    // Sits after the B/L because that is where the two diverge: an EXPORT
    // link is keyed on a BOOKING in the same column an IMPORT link keys on a
    // bill of lading. Without this, the two are indistinguishable on the page.
    // `accessorFn` (not accessorKey) so the null case sorts and filters as the
    // same dash the cell renders, rather than as an empty string.
    {
      id: 'movetype',
      accessorFn: (r) => r.movetype ?? '—',
      header: 'Move Type',
      size: 110,
      cell: (i) => {
        const mt = i.row.original.movetype;
        if (!mt) {
          return (
            <span
              title="No matching order in CMS — the PRO was deleted or archived."
              className="text-xs text-muted"
            >
              —
            </span>
          );
        }
        return (
          <span
            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${
              MOVETYPE_STYLES[mt] ?? MOVETYPE_NEUTRAL
            }`}
          >
            {mt}
          </span>
        );
      },
    },
    { accessorKey: 'submitted_scac', header: 'SCAC', size: 90 },
    { accessorKey: 'submitted_rail', header: 'Terminal / Rail', size: 130 },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      size: 130,
      cell: (i) => {
        const s = STATUS_STYLES[i.row.original.status];
        return (
          <span title={s.hint} className={`inline-flex px-2 py-0.5 text-xs font-medium rounded ${s.cls}`}>
            {s.label}
          </span>
        );
      },
    },
    {
      id: 'last_synced_at',
      accessorKey: 'last_synced_at',
      header: 'Last synced',
      size: 110,
      cell: (i) => (
        <span className="text-xs text-gray-600 dark:text-gray-400">
          {formatSynced(i.row.original.last_synced_at)}
        </span>
      ),
    },
    {
      id: 'gwitrace_shipment_id',
      accessorKey: 'gwitrace_shipment_id',
      header: 'gwiTrace shipment',
      size: 300,
      cell: (i) => (
        <span className="text-[0.6875rem] text-muted">
          {i.row.original.gwitrace_shipment_id}
        </span>
      ),
    },
  ], []);

  const carrierOptions = useMemo(() => {
    const seen = new Set<string>();
    return carriers
      .filter((c) => (seen.has(c.code) ? false : (seen.add(c.code), true)))
      .sort((a, b) => a.code.localeCompare(b.code));
  }, [carriers]);

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-body">Tracking Links</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 max-w-4xl">
          Clears the <code>order_tracking_link</code> row left behind after you delete a container in
          gwiTrace — the second half of a delete-and-resubmit. <strong>Delete the box in gwiTrace
          first</strong>; only links whose shipment is confirmed gone can be removed here, so a live
          trace can&apos;t be unlinked by accident.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-panel rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="min-w-[190px]">
            <label className="block text-xs font-medium text-label mb-1">
              Carrier / Terminal
            </label>
            <select
              value={carrier}
              onChange={(e) => { setCarrier(e.target.value); setOffset(0); }}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-body"
            >
              <option value="">— all carriers —</option>
              {carrierOptions.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.code}{c.name ? ` — ${c.name}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[280px]">
            <label className="block text-xs font-medium text-label mb-1">
              Pro / Container / BOL / Booking — one, or many separated by comma / space / newline
            </label>
            <input
              type="text"
              value={containers}
              onChange={(e) => setContainers(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setOffset(0); load(); } }}
              placeholder="5112638, MRSU9240462, 2173219890 …"
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-body"
            />
          </div>

          <div className="min-w-[150px]">
            <label className="block text-xs font-medium text-label mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as any); setOffset(0); }}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-900 text-body"
            >
              <option value="all">All</option>
              <option value="orphaned">Orphaned only</option>
              <option value="linked">Still linked</option>
            </select>
          </div>

          <button
            onClick={() => { setOffset(0); load(); }}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white"
          >
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Search
          </button>
        </div>
      </div>

      {probeFailed && (
        <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
          <p className="text-sm text-amber-800 dark:text-amber-300">
            Couldn&apos;t reach gwiTrace to check whether these shipments still exist, so every row
            shows as <strong>Unchecked</strong> and nothing can be deleted. This is deliberate —
            without a verified answer, deleting could unlink a live trace.
          </p>
        </div>
      )}

      {/* Selection bar */}
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 p-3 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <span className="text-sm text-blue-900 dark:text-blue-200">
            {selected.length} selected
            {blockedCount > 0 && (
              <span className="text-blue-700 dark:text-blue-300">
                {' '}· {blockedCount} not deletable (shipment still in gwiTrace)
              </span>
            )}
          </span>
          <button
            onClick={onDelete}
            disabled={deleting || deletable.length === 0}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed text-white"
          >
            <TrashIcon className="h-4 w-4" />
            {deleting ? 'Deleting…' : `Delete ${deletable.length} orphaned link${deletable.length === 1 ? '' : 's'}`}
          </button>
        </div>
      )}

      {/* Sweep delete — independent of the per-page selection above, because
          that selection is capped at the rendered 100 rows and is cleared on
          every page change. */}
      {total > 0 && (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <button
            onClick={onDeleteAllMatching}
            disabled={bulkBusy || deleting || probeFailed}
            title={probeFailed
              ? 'gwiTrace is unreachable, so orphan status is unverified'
              : 'Delete every ORPHANED link matching the current filters, across all pages'}
            className="inline-flex items-center gap-2 px-3 py-1.5 font-medium rounded-md border border-red-300 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <TrashIcon className="h-4 w-4" />
            {bulkBusy ? (bulkNote || 'Working…') : 'Delete all orphaned matching filter'}
          </button>
          <span className="text-gray-500 dark:text-gray-400">
            Across every page, not just the {PAGE_SIZE} shown.
          </span>
        </div>
      )}

      {toast && (
        <div className={`p-3 rounded-md text-sm ${
          toast.kind === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          {toast.msg}
        </div>
      )}

      <TanTable
        data={rows}
        columns={columns}
        loading={loading}
        errorMessage={error}
        onRetry={load}
        settingsKey="tracing-tracking-links"
        searchPlaceholder="Search links…"
        enableRowSelection
        onSelectionChange={setSelected}
        getRowId={(r: TrackingLinkRow) => String(r.pronumber)}
        cardTitle={`Tracking Links (${rows.length}${total > rows.length ? ` of ${total}` : ''})`}
      />

      {/* Paging — the existence probe runs per page, so paging is explicit
          rather than infinite-scroll: each page costs one gwiTrace round-trip. */}
      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <span>
            Showing {offset + 1}–{offset + rows.length} of {total} matching link{total === 1 ? '' : 's'}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0 || loading}
              className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total || loading}
              className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackingLinks;
